import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog'
import { Button } from 'components/ui/button'
import useToast from 'hooks/useToast'
import { createAdjustment } from 'submission/lib/partner-api'

type Props = {
  open: boolean
  partnerId: number
  onClose: () => void
  onCreated: () => void
}

const inputCls =
  'w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary'

const DECIMAL = /^-?\d+(\.\d{1,6})?$/

const AdjustmentModal: React.FC<Props> = ({
  open,
  partnerId,
  onClose,
  onCreated,
}) => {
  const { showToast } = useToast()
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      setAmount('')
      setReason('')
      setError(null)
    }
  }, [open])

  const valid =
    DECIMAL.test(amount.trim()) &&
    Number(amount) !== 0 &&
    reason.trim().length > 0

  const submit = async () => {
    if (!valid) return
    setSubmitting(true)
    setError(null)
    try {
      await createAdjustment(partnerId, {
        amount_usdc: amount.trim(),
        reason: reason.trim(),
      })
      showToast({ type: 'success', message: 'Adjustment recorded' })
      setAmount('')
      setReason('')
      onCreated()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Add adjustment</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <label className="block text-sm">
            Amount (USDC, negative to deduct)
            <input
              className={inputCls}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="-12.50"
            />
          </label>
          <label className="block text-sm">
            Reason
            <input
              className={inputCls}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          {error && <div className="text-sm text-red-600">{error}</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!valid || submitting}>
            Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default AdjustmentModal
